import { Amount } from "../../../../../amount/amount";
import { USSD_CODE_REGEX } from "../../../../../utils/regex";
import { isPlainObject, getValueAtPath, objectWithAccessors, withProperties } from "../../../../../utils";
import { DEFAULT_POST_INIT, isJsonResponse, withSharedHeaders } from "../../../../../fetcher/utils";
import { falseFunction } from "../../../../../collections/helpers";
import Fetcher from "../../../../../fetcher";
import english from "../../../../../../locales/en/instructions.json"
import french from "../../../../../../locales/fr/instructions.json"
import portuguese from "../../../../../../locales/pt/instructions.json"
import { fetchTranslatedString } from "../../../../../../support/helpers";
import Device from "../../../../../init/device";
import EventLog from "../../../../../logger/events";
import { getBeneficiaryFromNote } from "../../../../../utils/beneficiary";

const $fetcher = withSharedHeaders(Fetcher.one.init(DEFAULT_POST_INIT));

function getAccountName(note) {
  if (!note.includes("to")) return note;

  const extract = note.split("to")[1];
  const name = extract.split(' ').filter(Boolean).join(' ');
  return name;
}

export default function __onRequireValidation__() {
  const $this = this;

  return function onRequireValidation() {
    if (isPlainObject(this)) {
      const { data, reference, validation_type: validationType } = this;
      const paymentMethod = getValueAtPath($this.STATE, 'method.current');
      const currentLocale = getValueAtPath($this.STATE, 'locale');

      $this.$$data = data;
      $this.reference = reference;

      switch (paymentMethod) {
        case 'banktransfer': {
          const amount = getValueAtPath(data, 'amount');
          const currency = getValueAtPath($this.DATA, 'currency') || 'NGN';
          const beneficiary = getValueAtPath($this.DATA, 'banktransfer_beneficiary_name');
          const accountNumberExpiryTime = getValueAtPath($this.DATA, 'expires');

          if (currency === 'NGN' || currency === 'USD' || currency === 'GHS') {
            const accountnumber = getValueAtPath(data, 'accountnumber');
            const bankname = getValueAtPath(data, 'bankname');

            // MOR-mapped GHS merchants settle into a Flutterwave-owned account, and the charge response only names it inside `note`.
            const transferNote = currency === 'GHS' ? getValueAtPath(data, 'note') : null;
            const morBeneficiary = transferNote ? getBeneficiaryFromNote(transferNote) : null;
            if (transferNote && !morBeneficiary) {
              EventLog.beneficiaryExtractionFailed(transferNote);
            }

            if (accountnumber && amount && bankname) {
              const chargeAmount = new Amount(amount, currency);

              $this.TRANSFER_DETAILS = objectWithAccessors({
                account: () => accountnumber,
                amount: () => chargeAmount,
                bank: () => bankname,
                beneficiary: () => morBeneficiary || beneficiary,
                expiryTime: () => accountNumberExpiryTime,
              });
            }
          } else if (currency === 'EGP' || currency === 'KES' ) {
            const accountnumber = getValueAtPath(data, 'accountnumber');
            const bankname = getValueAtPath(data, 'bankname');
            const note = getValueAtPath(data, 'note');
            const narration = getValueAtPath(data, 'customer_ref');

            if (accountnumber && amount && bankname) {
              const chargeAmount = new Amount(amount, currency);

              $this.TRANSFER_DETAILS = objectWithAccessors({
                account: () => accountnumber,
                amount: () => chargeAmount,
                bank: () => bankname,
                account_name: () => note !== null ? getAccountName(note) : null,
                narration: () => narration
              });
            }
          } else {
            const amount = getValueAtPath(data, 'data.amount');
            const payment_code = getValueAtPath(data, 'data.payment_code');
            const accountNumberExpiryTime = getValueAtPath($this.DATA, 'expires');

            if (amount && payment_code) {
              const chargeAmount = new Amount(amount, currency);

              const paymentDetails = objectWithAccessors({
                amount: () => chargeAmount,
                beneficiary: () => beneficiary,
                payment_code: () => payment_code,
                expiryTime: () => accountNumberExpiryTime,
              }, false);

              if (currency === 'GBP') {
                withProperties(paymentDetails, {
                  account_number: '43271228',
                  sort_code: '040053'
                });
              } else {
                withProperties(paymentDetails, {
                  iban: 'GB11PAYR00997500000697',
                  bic_swift: 'PAYRGB21'
                });
              }

              $this.TRANSFER_DETAILS = Object.freeze(paymentDetails);
            }
          }

          break;
        }

        case 'enaira': {
          const paymentOption = getValueAtPath($this.$inputs, 'eNairaPaymentOption[value].identifier');
          switch (paymentOption) {
            case 'QR': {
              const qrcode = getValueAtPath(data, 'qr_image');
              qrcode && ($this.QR_CODE = qrcode); 
              break;
            }

            case 'TOKEN': {
              $this.SHOULD_VALIDATE_CHARGE = true;
              break;
            }
          }
          break;
        }

        case 'mobilemoneyghana':
        case 'mobilemoneyrwanda':
        case 'mobilemoneytanzania':
        case 'mobilemoneyuganda':
        case 'mobilemoneyzambia':
        case 'mpesa':
        case 'payattitude': {
          const selectedNetwork = getValueAtPath($this.$inputs, '[network].value.identifier');
          let validateInstructions;

          if (paymentMethod === 'mobilemoneyghana') {
            if (selectedNetwork === 'MTN') {
                $this.MOMO_INSTRUCTIONS = 'Please dial *170# to confirm your transaction if the prompt fails.';
            } else {
              validateInstructions = getValueAtPath(data, 'validateInstructions');
              const [code = '#144#'] = [].concat(String(validateInstructions).match(/[*#][\d*#]+[*#]/g)).filter(Boolean);
              $this.MOMO_INSTRUCTIONS = `Dial ${code} on your mobile phone to authorize this payment.`;
            }

            const [code = '#144#'] = [].concat(String(validateInstructions).match(/[*#][\d*#]+[*#]/g)).filter(Boolean);
            let languageFileString = "authorize.momo";
            // let currentLocaleFile = currentLocale === 'en' ? english : french;
            let currentLocaleFile = currentLocale === 'en' ? english : currentLocale === 'fr' ? french : portuguese;

            const instructions = fetchTranslatedString(currentLocaleFile, languageFileString);
            const instructionsWithCode = instructions.split(' ');
            instructionsWithCode.splice(1, 0, code);
            $this.MOMO_INSTRUCTIONS = instructionsWithCode.join(' ');
            // if (selectedNetwork === 'MTN') {
            //   // Wait for some time, say 20(s) and then update momo instructions.
            //   setTimeout(() => {
            //     $this.MOMO_INSTRUCTIONS = [
            //       'Dial *170#',
            //       'Choose Wallet → (3) My Approvals',
            //       'Enter your MOMO PIN to retrieve your pending approval list',
            //       'Choose a pending transaction',
            //       'Choose option (1) to approve',
            //       'Tap button to continue',
            //       'Wait for update on your payment'
            //     ];
            //   }, 20 * 1000);
            // }
          }

          if (paymentMethod === 'mpesa') {
            let languageFileString = "mpesa.notification";
            // let currentLocaleFile = currentLocale === 'en' ? english : french;
            let currentLocaleFile = currentLocale === 'en' ? english : currentLocale === 'fr' ? french : portuguese;
            let message = fetchTranslatedString(currentLocaleFile, languageFileString);
            let useFallback = false;

            $this.MPESA_SECONDARY_INSTRUCTION = null;

            if (selectedNetwork === 'SAFARICOM') {
              const canSendSTKPushNotification = Boolean(getValueAtPath($this.DATA, 'canSendSTKPushNotification'));
              const mpesaPaybill = getValueAtPath($this.DATA, 'mpesaPaybill');

              useFallback = getValueAtPath($this.DATA, 'preferences[ignore_mpesa_fallback]') !== true;
              $this.MPESA_INSTRUCTION = canSendSTKPushNotification ? message : null;

              const accountnumber = $this.reference.order;
              const businessnumber = mpesaPaybill || getValueAtPath(data, 'data.business_number');
              const amount = getValueAtPath(data, 'charged_amount');

              if (useFallback && accountnumber && businessnumber && amount) {
                const fallbackDuration = canSendSTKPushNotification ? 20 : 0;
                const currency = getValueAtPath($this.DATA, 'currency');
                const chargeAmount = new Amount(amount, currency);
                // const currentLocaleFile = currentLocale === 'en' ? english : french;
                let currentLocaleFile = currentLocale === 'en' ? english : currentLocale === 'fr' ? french : portuguese;

                const withoutFallbackDurationLangFileString = "mpesa.secondary.without.fallbackDuration";

                const withFallbackDurationLangFileString = "mpesa.secondary.with.fallbackDuration";
                const actualFallbackDurationMessage = fetchTranslatedString(currentLocaleFile, withFallbackDurationLangFileString);
                let messageWithFallbackDuration = actualFallbackDurationMessage.split(' ');
                messageWithFallbackDuration.splice(7, 0, fallbackDuration);

                $this.MPESA_SECONDARY_INSTRUCTION = fallbackDuration
                  ? messageWithFallbackDuration.join(' ')
                  : fetchTranslatedString(currentLocaleFile, withoutFallbackDurationLangFileString);
                
                $this.FALLBACK_DETAILS = objectWithAccessors({
                  account: () => accountnumber,
                  amount: () => chargeAmount,
                  business: () => businessnumber
                });

                // Wait for some time, say 20(s) and then override M-PESA flow.
                $this.mpesaOverrideTimeout = setTimeout(() => {
                  ($this.mpesaPollingCompleted !== true) && Promise.resolve(
                    $fetcher(new URL('/v2/gpx/mpesa/authoverride', process.env.API_BASE_URL), {
                      body: JSON.stringify({
                        modalauditid: getValueAtPath($this.DATA, 'modalauditid'),
                        public_key: getValueAtPath($this.DATA, 'PBFPubKey'),
                        id: getValueAtPath(data, 'id')
                      })
                    })
                      .then(response => (isJsonResponse(response) && response.ok))
                      .catch(falseFunction)
                  );
                }, fallbackDuration * 1000);
              }
            } else { $this.MPESA_INSTRUCTION = message }
          }

          break;
        }

        case 'mobilemoneyfranco': {
          if (validationType === 'twofactorvalidation') {
            $this.SHOULD_VALIDATE_CHARGE = true;
          } else if (validationType === 'callback') {
            const country = getValueAtPath($this.$inputs, '[country].value.code');
            const selectedNetwork = getValueAtPath($this.$inputs, '[network].value.identifier');
            const visitDevice = Device.isMobile;

            if (country === 'SN' && selectedNetwork === 'Orangemoney' && visitDevice) {
              const redirectUrl = getValueAtPath(data, 'data.redirect_url');
              redirectUrl && ($this.REDIRECT_URL = new URL (redirectUrl));
            } else if (country === 'SN' && selectedNetwork === 'Orangemoney' && !visitDevice) {
              const qrcode = getValueAtPath(data, 'data.qr_code');
              qrcode && ($this.QR_CODE = qrcode);
            } else if (country !== 'BF') {
              const note = getValueAtPath(data, 'data.note');
              const [code = '#144#'] = [].concat(String(note).match(/[*#][\d*#]+[*#]/g)).filter(Boolean);
              let languageFileString = "authorize.momo";
              // let currentLocaleFile = currentLocale === 'en' ? english : french;
              let currentLocaleFile = currentLocale === 'en' ? english : currentLocale === 'fr' ? french : portuguese;
              const instructions = fetchTranslatedString(currentLocaleFile, languageFileString);
              const instructionsWithCode = instructions.split(' ');
              instructionsWithCode.splice(1, 0, code);
              $this.CONFIRM_INSTRUCTION = instructionsWithCode.join(' ');
            }

            $this.SHOULD_VALIDATE_CHARGE = false;
          }

          break;
        }

        case 'paga': {
          if (validationType === 'twofactorvalidation') {
            $this.PAGA_INSTRUCTION = getValueAtPath(data, 'data.note') || null;
            $this.SHOULD_VALIDATE_CHARGE = true;
          }

          break;
        }

        case 'nqr':
        case 'qr':
        case 'wechat': {
          const qrcode = getValueAtPath(data, 'qr_image');
          qrcode && ($this.QR_CODE = qrcode);
          break;
        }

        case 'ussd': {
          const note = getValueAtPath(data, 'data.note');
          const [, ussdCode, referenceCode] = String(note).match(USSD_CODE_REGEX) || [];

          const code = (
            referenceCode
              ? (ussdCode || String()).replace(/#$/, `*${referenceCode}#`)
              : ussdCode
          ) || null;

          code && ($this.USSD_CODE = code);
          break;
        }

        case 'fawrypay': {
          const reference = getValueAtPath(data, 'flwRef');
          reference && ($this.FAWRY_REFERENCE = reference);
          break;
        }

        case 'capitec': {
          $this.SHOULD_VALIDATE_CHARGE = true
        }
      }
    }
  }
}
